// @tg-seq: 11928

/**
 * Mobile App Developer Portal (Integration Hub)
 *
 * Public, standalone page for EXTERNAL mobile-app developers. NOT part of
 * the pfUser auth system -- exchanges a Mobile App Registry API_Client_ID +
 * secret (issued via the admin "API Access" tab on
 * scr_tgc_10409_mobile_app_master.tsx) for a short-lived app_access token,
 * then lists that app's whitelisted screens and lets the developer download
 * the cached .tsx source for each one.
 *
 * Backend:
 *   POST /api/mobile-app-auth/token   (routes_mobile_app_auth.py)
 *   POST /api/mobile-app-portal/      (routers/infrastructure/backend_990595.py)
 */
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';
import React, { useState } from 'react';
import { getTsxFileName } from '../../utils/componentIdentity';

interface ScreenAssignment {
    mobile_app_screen_assignment_id: number;
    sequence_num: number;
    friendly_name: string;
    tsxfilename_target: string;
    endpoint_path: string | null;
    has_source_snapshot: number;
}

const Self: React.FC = () => {
    const [clientId, setClientId] = useState('');
    const [secret, setSecret] = useState('');
    const [appToken, setAppToken] = useState<string | null>(null);
    const [appKey, setAppKey] = useState<string>('');
    const [screens, setScreens] = useState<ScreenAssignment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingSeq, setDownloadingSeq] = useState<number | null>(null);
    const [isDownloadingPacket, setIsDownloadingPacket] = useState(false);
    const [integrationGuide, setIntegrationGuide] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const apiBase = import.meta.env.VITE_API_BASE_URL || '';

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const tokenResponse = await fetch(`${apiBase}/api/mobile-app-auth/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ api_client_id: clientId, api_secret: secret }),
            });
            const tokenData = await tokenResponse.json();

            if (!tokenResponse.ok) {
                throw new Error(tokenData.detail || 'Invalid API client credentials');
            }

            setAppToken(tokenData.access_token);
            setAppKey(tokenData.app_key);

            const manifestResponse = await fetch(`${apiBase}/api/mobile-app-portal/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenData.access_token}`,
                },
                body: JSON.stringify({ manage: 1 }),
            });
            const manifestData = await manifestResponse.json();

            if (!manifestResponse.ok) {
                throw new Error(manifestData.detail || 'Failed to load screen manifest');
            }

            setScreens(manifestData.data || []);
            setIntegrationGuide(manifestData.integration_guide || null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (row: ScreenAssignment) => {
        if (!appToken) return;
        setDownloadingSeq(row.sequence_num);
        setError(null);

        try {
            const response = await fetch(`${apiBase}/api/mobile-app-portal/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${appToken}`,
                },
                body: JSON.stringify({ manage: 2, sequence_num: row.sequence_num }),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Download failed');
            }

            const blob = new Blob([data.data.source_text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = row.tsxfilename_target || `screen_${row.sequence_num}.tsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Download failed');
        } finally {
            setDownloadingSeq(null);
        }
    };

    // Bundles the manifest + every synced screen's source into ONE JSON file,
    // so the developer (or their own tooling) grabs everything in one shot
    // instead of looping per-row downloads. Mirrors the agentic-mfg-floor
    // "workorder packet" pattern (compute once server-side, hand back one
    // self-contained artifact).
    const handleDownloadPacket = async () => {
        if (!appToken) return;
        setIsDownloadingPacket(true);
        setError(null);

        try {
            const response = await fetch(`${apiBase}/api/mobile-app-portal/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${appToken}`,
                },
                body: JSON.stringify({ manage: 3 }),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to build integration packet');
            }

            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${appKey || 'integration'}_packet.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to build integration packet');
        } finally {
            setIsDownloadingPacket(false);
        }
    };

    const columns: GridColDef[] = [
        { field: 'sequence_num', headerName: 'Seq', width: 90 },
        { field: 'friendly_name', headerName: 'Screen', flex: 1, minWidth: 200 },
        { field: 'tsxfilename_target', headerName: 'File', flex: 1, minWidth: 220 },
        { field: 'endpoint_path', headerName: 'API Endpoint', flex: 1, minWidth: 200 },
        {
            field: 'actions',
            headerName: 'Source',
            width: 160,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={
                        !params.row.has_source_snapshot ||
                        downloadingSeq === params.row.sequence_num
                    }
                    onClick={() => handleDownload(params.row)}
                >
                    {downloadingSeq === params.row.sequence_num
                        ? '...'
                        : params.row.has_source_snapshot
                          ? 'Download'
                          : 'Not synced'}
                </Button>
            ),
        },
    ];

    const rows: GridRowsProp = screens.map((s) => ({
        id: s.mobile_app_screen_assignment_id,
        ...s,
    }));

    if (!appToken) {
        return (
            <Container maxWidth="xs" sx={{ mt: 10 }}>
                <Paper sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LockIcon />
                        <Typography variant="h5">Developer Portal</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        Enter your app's API Client ID and secret to access your whitelisted
                        screens.
                    </Typography>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box component="form" onSubmit={handleLogin}>
                        <TextField
                            label="API Client ID"
                            fullWidth
                            required
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="API Secret"
                            type="password"
                            fullWidth
                            required
                            value={secret}
                            onChange={(e) => setSecret(e.target.value)}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
                        >
                            {isLoading ? 'Connecting...' : 'Connect'}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Welcome, {appKey}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These are the screens whitelisted for your app. The integration packet bundles an
                app starter (core integration files) plus every screen below, prewired and ready to
                drop into your project.
            </Typography>
            {integrationGuide && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {integrationGuide}
                </Alert>
            )}
            <Button
                variant="contained"
                startIcon={isDownloadingPacket ? <CircularProgress size={18} /> : <DownloadIcon />}
                disabled={isDownloadingPacket}
                onClick={handleDownloadPacket}
                sx={{ mb: 3 }}
            >
                {isDownloadingPacket ? 'Building packet...' : 'Download Integration Packet (.json)'}
            </Button>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            <Paper sx={{ p: 1 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    sx={{ height: 500, width: '100%', bgcolor: 'background.paper' }}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Paper>
        </Container>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
